import React from 'react';
import { formatUKDate, formatUKDateTime, formatUKCurrency } from '../../utils/formatters';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import {
  ShoppingCart as OrderIcon,
  LocalShipping as ShippingIcon,
  Assignment as ReturnIcon,
  Info as InfoIcon,
  CheckCircle as ApprovedIcon,
  Schedule as ProcessingIcon,
} from '@mui/icons-material';

const AmazonReturnDetails = ({ returnData }) => {
  const isAmazonReturn = returnData?.platform === 'Amazon Upload' || 
                        returnData?.platform === 'Amazon SP-API' || 
                        returnData?.platform === 'Amazon';
  
  if (!returnData || !isAmazonReturn) {
    return null;
  }

  // Parse label details if it's a JSON string
  let labelDetails = {};
  try {
    labelDetails = typeof returnData.label_details === 'string' 
      ? JSON.parse(returnData.label_details) 
      : returnData.label_details || {};
  } catch (e) {
    labelDetails = {};
  }

  const formatDate = formatUKDateTime;

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'approved':
        return <ApprovedIcon sx={{ color: '#4caf50' }} />;
      case 'processing':
      case 'pending':
        return <ProcessingIcon sx={{ color: '#ff9800' }} />;
      default:
        return <InfoIcon sx={{ color: '#2196f3' }} />;
    }
  };

  const formatReturnReason = (code) => {
    const reasonMap = {
      'CR-UNWANTED_ITEM': 'Customer Return - Unwanted Item',
      'CR-NOT_COMPATIBLE': 'Customer Return - Not Compatible',
      'CR-QUALITY_UNACCEPTABLE': 'Customer Return - Quality Unacceptable',
      'CR-DEFECTIVE': 'Customer Return - Defective',
      'CR-SWITCHEROO': 'Customer Return - Switcheroo',
      'AMZ-PG-APP-TOO-SMALL': 'Amazon Program - Too Small',
      'AMZ-PG-APP-TOO-LARGE': 'Amazon Program - Too Large',
    };
    return reasonMap[code] || code || 'Unknown';
  };

  return (
    <Box sx={{ maxWidth: '100%', mt: 2 }}>
      <Paper sx={{ p: 3, mb: 2, border: '1px solid #FF9900' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: '#FF9900' }}>
          <ReturnIcon sx={{ mr: 1 }} />
          Amazon Return Details - {returnData.return_number}
        </Typography>

        <Grid container spacing={3}>
          {/* Item Details Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid #FFB74D' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: '#E65100' }}>
                📦 Item Details
              </Typography>
              <List dense>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" color="textSecondary">Item Name:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {returnData.product_name || (returnData.items && returnData.items[0]?.product_name) || 'N/A'}
                    </Typography>
                  </Box>
                </ListItem>
                
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" color="textSecondary">ASIN:</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {returnData.asin || 'N/A'}
                    </Typography>
                  </Box>
                </ListItem>

                <ListItem disablePadding sx={{ mb: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" color="textSecondary">Merchant SKU:</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {returnData.sku || (returnData.items && returnData.items[0]?.sku) || 'N/A'}
                    </Typography>
                  </Box>
                </ListItem>

                <ListItem disablePadding sx={{ mb: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" color="textSecondary">Return Reason:</Typography>
                    <Chip 
                      label={formatReturnReason(returnData.return_reason)} 
                      size="small" 
                      sx={{ mt: 0.5, backgroundColor: '#ffebee', color: '#c62828' }}
                    />
                  </Box>
                </ListItem>

                <ListItem disablePadding sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Quantity:</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF9900' }}>
                        {returnData.return_quantity || '1'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">In Policy:</Typography>
                      <Chip 
                        label={returnData.inPolicy === 'Y' ? 'Yes' : 'No'} 
                        size="small"
                        sx={{ 
                          backgroundColor: returnData.inPolicy === 'Y' ? '#e8f5e8' : '#ffebee',
                          color: returnData.inPolicy === 'Y' ? '#2e7d32' : '#c62828'
                        }}
                      />
                    </Box>
                  </Box>
                </ListItem>

                <ListItem disablePadding sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Resolution:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {returnData.resolution || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Category:</Typography>
                      <Typography variant="body1">
                        {returnData.category || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>

                {(returnData.refundAmount || returnData.refund_amount || (returnData.items && returnData.items[0]?.refund_amount)) && parseFloat(returnData.refundAmount || returnData.refund_amount || returnData.items[0]?.refund_amount || 0) > 0 && (
                  <ListItem disablePadding>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" color="textSecondary">Refund Amount:</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                        {formatUKCurrency(returnData.refundAmount || returnData.refund_amount || (returnData.items && returnData.items[0]?.refund_amount))}
                      </Typography>
                    </Box>
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Order & Return Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, bgcolor: '#f3e5f5', border: '1px solid #CE93D8' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: '#6A1B9A' }}>
                <OrderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Order & Return Information
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>Order ID:</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', border: 'none' }}>
                        {returnData.order_id || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>Order Date:</TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        {formatDate(returnData.orderDate)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>Amazon RMA ID:</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#FF9900', border: 'none' }}>
                        {returnData.amazon_rma_id || 'N/A'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>Return Request Date:</TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        {formatDate(returnData.return_date)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>Status:</TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(returnData.return_status)}
                          <Chip 
                            label={returnData.return_status || 'Unknown'} 
                            size="small"
                            sx={{
                              backgroundColor: returnData.return_status === 'completed' ? '#e8f5e8' : '#fff3e0',
                              color: returnData.return_status === 'completed' ? '#2e7d32' : '#e65100'
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>A-to-Z Claim:</TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        <Chip 
                          label={returnData.aToZClaim === 'true' ? 'Yes' : 'No'} 
                          size="small"
                          sx={{
                            backgroundColor: returnData.aToZClaim === 'true' ? '#ffebee' : '#e8f5e8',
                            color: returnData.aToZClaim === 'true' ? '#c62828' : '#2e7d32'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>Prime Order:</TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        <Chip 
                          label={returnData.isPrime === 'Y' ? 'Yes' : 'No'} 
                          size="small"
                          sx={{
                            backgroundColor: returnData.isPrime === 'Y' ? '#e3f2fd' : '#f5f5f5',
                            color: returnData.isPrime === 'Y' ? '#0277bd' : '#666'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>Return Type:</TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        {returnData.returnType || 'N/A'}
                      </TableCell>
                    </TableRow>
                    {returnData.orderAmount && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', border: 'none', pl: 0 }}>Order Amount:</TableCell>
                        <TableCell sx={{ border: 'none', fontWeight: 'bold', color: '#4caf50' }}>
                          £{parseFloat(returnData.orderAmount).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Label Details Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: '#e8f5e8', border: '1px solid #81C784' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                <ShippingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Label Details
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Tracking ID:</Typography>
                    <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#FF9900' }}>
                      {labelDetails.tracking_id || returnData.tracking_number || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Return Carrier:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {labelDetails.return_carrier || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Label Cost:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                      {labelDetails.currency_code || 'GBP'} {labelDetails.label_cost || '0.00'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Label Type:</Typography>
                    <Chip 
                      label={labelDetails.label_type || 'N/A'} 
                      size="small"
                      sx={{ backgroundColor: '#fff3e0', color: '#e65100' }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Label Paid By:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {returnData.labelToBePaidBy || 'Customer'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default AmazonReturnDetails;